import { COMPARISON_FILTER_DATA_KEY } from '~/types';
import { textValueOf } from '~/types/filters';
import {
  newComparisonFilterRowId,
  operatorsForField,
  type ComparisonFilterConnector,
  type ComparisonFilterRow,
} from '~/pages/order-builder/lib/comparisonFilters';

export type SavedComparisonFilterRow = {
  field: string;
  operator: string;
  value: string;
  connector?: string;
};

export type ComparisonFilterData = {
  comparisonFilters: SavedComparisonFilterRow[];
};

export function rowsToFilterData(
  rows: ComparisonFilterRow[]
): ComparisonFilterData {
  return {
    [COMPARISON_FILTER_DATA_KEY]: rows.map((row) => ({
      field: row.field,
      operator: row.operator,
      value: textValueOf(row),
      connector: row.connector,
    })),
  };
}

function isSavedRow(entry: unknown): entry is SavedComparisonFilterRow {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const candidate = entry as Record<string, unknown>;
  return (
    typeof candidate.field === 'string' &&
    typeof candidate.operator === 'string' &&
    typeof candidate.value === 'string'
  );
}

function connectorOf(
  entry: SavedComparisonFilterRow
): ComparisonFilterConnector {
  return entry.connector === 'or' ? 'or' : 'and';
}

export function toFilterRows(raw: unknown): ComparisonFilterRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const rows: ComparisonFilterRow[] = [];
  for (const entry of raw) {
    if (!isSavedRow(entry)) {
      continue;
    }
    const operators = operatorsForField(entry.field);
    if (operators === null || !operators.includes(entry.operator)) {
      continue;
    }
    rows.push({
      id: newComparisonFilterRowId(),
      field: entry.field,
      operator: entry.operator,
      value: entry.value,
      connector: connectorOf(entry),
    });
  }
  return rows;
}

export function readComparisonFilterRows(
  filterData: unknown
): ComparisonFilterRow[] {
  if (typeof filterData !== 'object' || filterData === null) {
    return [];
  }
  return toFilterRows(
    (filterData as Record<string, unknown>)[COMPARISON_FILTER_DATA_KEY]
  );
}
